import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { FaCalendarAlt, FaChevronLeft, FaChevronRight, FaTimes } from 'react-icons/fa';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Professional month-year picker rendered via React portal.
 * value / onChange use "MMM-YYYY" string format (e.g. "Mar-2026").
 * Pass clearable={true} to allow clearing the selection.
 */
const MonthYearPicker = ({
  value = '',
  onChange,
  placeholder = 'Select month',
  clearable = true,
  disabled = false,
  inputClassName = '',
  minWidth = 148,
  size = 'md',      // 'md' (default) | 'sm' — 'sm' matches native text-xs filter inputs (~30px)
  minDate = null,   // "MMM-YYYY" — months before this are disabled
  maxDate = null,   // "MMM-YYYY" — months after this are disabled
}) => {
  const sm = size === 'sm';
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(() => {
    const y = value ? dayjs(value, 'MMM-YYYY').year() : dayjs().year();
    return isNaN(y) ? dayjs().year() : y;
  });

  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });

  // Sync viewYear when value changes externally
  useEffect(() => {
    if (value) {
      const y = dayjs(value, 'MMM-YYYY').year();
      if (!isNaN(y)) setViewYear(y);
    }
  }, [value]);

  const openPicker = useCallback(() => {
    if (disabled) return;
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: Math.max(rect.width, 220),
      });
    }
    setOpen(true);
  }, [disabled]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        popoverRef.current && !popoverRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Reposition on scroll/resize
  useEffect(() => {
    if (!open) return;
    const reposition = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setCoords({
          top: rect.bottom + window.scrollY + 4,
          left: rect.left + window.scrollX,
          width: Math.max(rect.width, 220),
        });
      }
    };
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open]);

  const parsed = value ? dayjs(value, 'MMM-YYYY') : null;
  const selectedMonth = parsed?.isValid() ? parsed.month() : null; // 0-indexed
  const selectedYear  = parsed?.isValid() ? parsed.year()  : null;

  const minD = minDate ? dayjs(minDate, 'MMM-YYYY').startOf('month') : null;
  const maxD = maxDate ? dayjs(maxDate, 'MMM-YYYY').startOf('month') : null;

  const isMonthDisabled = (monthIdx) => {
    const candidate = dayjs().year(viewYear).month(monthIdx).startOf('month');
    if (minD && candidate.isBefore(minD)) return true;
    if (maxD && candidate.isAfter(maxD))  return true;
    return false;
  };

  const handleSelect = (monthIdx) => {
    if (isMonthDisabled(monthIdx)) return;
    const newVal = dayjs().year(viewYear).month(monthIdx).format('MMM-YYYY');
    onChange(newVal);
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
  };

  const displayLabel = parsed?.isValid()
    ? parsed.format('MMM-YYYY')
    : null;

  const popover = open ? ReactDOM.createPortal(
    <div
      ref={popoverRef}
      style={{
        position: 'absolute',
        top: coords.top,
        left: coords.left,
        width: coords.width,
        zIndex: 9999999,
      }}
      className="tw-bg-white tw-rounded tw-shadow-xl tw-border tw-border-gray-200 tw-overflow-hidden"
    >
      {/* Year navigation */}
      <div className="tw-flex tw-items-center tw-justify-between tw-px-3 tw-py-2 tw-bg-blue-600">
        <button
          type="button"
          className="tw-w-7 tw-h-7 tw-rounded-sm tw-flex tw-items-center tw-justify-center tw-border-0 tw-bg-blue-500 hover:tw-bg-blue-400 tw-text-white tw-cursor-pointer tw-transition-colors"
          onClick={() => setViewYear((y) => y - 1)}
        >
          <FaChevronLeft size={10} />
        </button>
        <span className="tw-font-bold tw-text-white tw-text-sm">{viewYear}</span>
        <button
          type="button"
          className="tw-w-7 tw-h-7 tw-rounded-sm tw-flex tw-items-center tw-justify-center tw-border-0 tw-bg-blue-500 hover:tw-bg-blue-400 tw-text-white tw-cursor-pointer tw-transition-colors"
          onClick={() => setViewYear((y) => y + 1)}
        >
          <FaChevronRight size={10} />
        </button>
      </div>

      {/* Month grid */}
      <div className="tw-grid tw-grid-cols-4 tw-gap-1 tw-p-2">
        {MONTHS.map((m, idx) => {
          const isSelected     = idx === selectedMonth && viewYear === selectedYear;
          const isCurrentMonth = idx === dayjs().month() && viewYear === dayjs().year();
          const disabled       = isMonthDisabled(idx);
          return (
            <button
              key={m}
              type="button"
              onClick={() => handleSelect(idx)}
              disabled={disabled}
              title={disabled ? 'Not available for this reporting period' : undefined}
              className={`
                tw-py-1.5 tw-rounded-sm tw-text-xs tw-font-medium tw-border-0 tw-transition-colors
                ${disabled
                  ? 'tw-bg-gray-100 tw-text-gray-300 tw-cursor-not-allowed'
                  : isSelected
                  ? 'tw-bg-blue-600 tw-text-white tw-cursor-pointer'
                  : isCurrentMonth
                  ? 'tw-bg-blue-50 tw-text-blue-700 tw-ring-1 tw-ring-blue-300 tw-cursor-pointer'
                  : 'tw-bg-gray-50 tw-text-gray-700 hover:tw-bg-blue-100 hover:tw-text-blue-700 tw-cursor-pointer'
                }
              `}
            >
              {m}
            </button>
          );
        })}
      </div>

      {/* Clear option */}
      {clearable && value && (
        <div className="tw-border-t tw-border-gray-100 tw-px-2 tw-py-1.5">
          <button
            type="button"
            onClick={() => { onChange(''); setOpen(false); }}
            className="tw-w-full tw-text-xs tw-text-gray-500 hover:tw-text-red-600 tw-flex tw-items-center tw-justify-center tw-gap-1 tw-border-0 tw-bg-transparent tw-cursor-pointer tw-py-0.5"
          >
            <FaTimes size={9} /> Clear selection
          </button>
        </div>
      )}
    </div>,
    document.body
  ) : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={openPicker}
        className={`tw-flex tw-items-center tw-gap-2 tw-border tw-rounded-md tw-bg-white tw-cursor-pointer tw-transition-colors tw-text-left
          ${sm ? 'tw-px-2 tw-py-1' : 'tw-px-3 tw-py-1.5'}
          ${open ? 'tw-border-blue-400 tw-ring-1 tw-ring-blue-200' : 'tw-border-gray-300 hover:tw-border-blue-400'}
          ${disabled ? 'tw-opacity-50 tw-cursor-not-allowed tw-bg-gray-50' : ''}
          ${inputClassName || ''}
        `}
        style={{ minWidth: minWidth || 148, minHeight: sm ? 30 : 34 }}
      >
        <FaCalendarAlt size={sm ? 10 : 11} className={`tw-flex-shrink-0 ${displayLabel ? 'tw-text-blue-500' : 'tw-text-gray-400'}`} />
        <span className={`tw-flex-1 tw-font-medium ${sm ? 'tw-text-xs' : 'tw-text-sm'} ${displayLabel ? 'tw-text-gray-800' : 'tw-text-gray-400'}`}>
          {displayLabel || placeholder || 'Select month'}
        </span>
        {clearable && displayLabel ? (
          <span
            role="button"
            tabIndex={0}
            className="tw-text-gray-400 hover:tw-text-gray-600 tw-leading-none tw-text-base"
            onClick={handleClear}
            onKeyDown={(e) => e.key === 'Enter' && handleClear(e)}
          >
            ×
          </span>
        ) : (
          <FaChevronLeft
            size={9}
            className={`tw-text-gray-400 tw-transition-transform ${open ? '-tw-rotate-90' : 'tw-rotate-180'}`}
            style={{ transform: open ? 'rotate(-90deg)' : 'rotate(-90deg) scaleX(-1)' }}
          />
        )}
      </button>
      {popover}
    </>
  );
};

MonthYearPicker.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  clearable: PropTypes.bool,
  disabled: PropTypes.bool,
  inputClassName: PropTypes.string,
  minWidth: PropTypes.number,
  size: PropTypes.oneOf(['md', 'sm']),
  minDate: PropTypes.string,
  maxDate: PropTypes.string,
};

export default MonthYearPicker;
